/* eslint-disable max-lines */
import React, { useContext, useEffect, useRef } from 'react';
import DatePicker from 'react-multi-date-picker';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import { FormControl } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core';
import moment from 'moment-jalaali';
import LanguageContext from '../../../contexts/languageContext';
import MessagesContext from '../../../contexts/messagesContext';
import { CalendarIcon } from '../Icons';
import persian from 'react-date-object/calendars/persian';
import gregorian from 'react-date-object/calendars/gregorian';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import persian_fa from 'react-date-object/locales/persian_fa';
import Footer from 'react-multi-date-picker/plugins/range_picker_footer';
import { ArrowBottomIcon } from '../Images/Icons';

const styles = makeStyles((theme) => ({
    rootFormControl: (props) => ({
        // width: props.withTime ? '200px' : '100%',
        width: props.width ? props.width : '180px',
        paddingLeft: props.width === '100%' || !props.rtl ? '0' : '30px',
        // paddingRight:  !props.rtl ? '30px' : '0',
        '&:after': {
            content: '" "'
        },
        [theme.breakpoints.down(1025)]: {
            width: '100%',
            paddingLeft: '0',
            paddingRight: '0'
        }
    }),
    label: {
        color: '#767676',
        paddingBottom: '8px'
    },
    datePickerContainer: ({ disabled }) => ({
        position: 'relative',
        borderRadius: '4px',
        border: 'solid 1.5px rgb(187,187,187)',
        display: 'flex',
        alignItems: 'center',
        opacity: disabled ? '0.5' : 1,
        background: disabled && '#fafbfc !important',
        '&:focus-within': {
            border: 'solid 1px #1b90fa'
        }
    }),
    datePicker: (props) => ({
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        width: props.width ? `calc(${props.width} - 10px - 10px)` : 'calc(180px - 30px - 22px)',
        color: '#3d454a',
        height: '1.67em',
        lineHeight: '1.67em',
        padding: '6px 10px 7px',
        boxSizing: 'content-box',
        fontSize: '16px',
        position: 'relative',
        fontFamily: 'inherit',
        direction: 'ltr',
        textAlign: props.rtl ? 'left' : 'right',
        '&:focus': {
            outline: 'none'
        }
    }),
    datePickerParent: {
        '& > div .rmdp-time-picker input': {
            fontFamily: 'inherit'
        },
        [theme.breakpoints.down(455)]: {
            '& > div .rmdp-header-values span': {
                fontSize: ({ rtl }) => !rtl && '11px'
            },
            '& > div .rmdp-day span': {
                fontSize: ({ rtl }) => !rtl && '11px'
            },
            '& > div .rmdp-week-day': {
                fontSize: ({ rtl }) => !rtl && '11px'
            }
        }
    },
    calendarIcon: (props) => ({
        position: 'absolute',
        right: props.rtl ? '6px' : 'auto',
        left: props.rtl ? 'auto' : '6px',
        top: '6px',
        height: '21px',
        width: '21px',
        // cursor: 'pointer',
        pointerEvents: 'none'
    }),
    inputClass: {
        fontSize: '14px !important'
    },
    errorContainer: {
        padding: '3px 7px',
        fontSize: '0.9em',
        color: 'red',
        minHeight: '26px'
    },
    placeholder: ({ rtl }) => ({
        position: 'absolute',
        right: rtl ? '0' : 'auto',
        left: rtl ? 'auto' : '0',
        marginInlineStart: '35px',
        top: '8px',
        fontSize: '13px',
        fontWeight: 500,
        pointerEvents: 'none'
    }),
    arrowIcon: {
        marginInlineEnd: '10px',
        height: '20px'
    }
}));
const noStyle = makeStyles({
    datePicker: {
        width: '80px',
        padding: '3px 0',
        border: 'solid 1px white',
        '&:focus': {
            border: 'solid 1px rgba(27, 144, 250, 0.7)',
            borderRadius: '4px'
        }
    }
});
export default function CalendarInput({
    label,
    id,
    name,
    value,
    setValue,
    onChange,
    usePortal = false,
    min,
    max,
    errors,
    hasTimePicker = false,
    isRangePicker = false,
    autoFocus = false,
    rangeValue,
    customClasses,
    format,
    isLeft = false,
    placeholder,
    disabled = false,
    ...props
}) {
    const { lang } = useContext(LanguageContext);
    const { messages } = useContext(MessagesContext);
    const datePickerRef = useRef();
    let classes;
    if (props.noStyle) {
        classes = noStyle();
    } else {
        classes = styles({ ...props, rtl: lang === 'fa', disabled });
    }
    const right = lang === 'fa' ? 'bottom-right' : 'bottom-left';
    const left = lang !== 'fa' ? 'bottom-right' : 'bottom-left';
    const handleChange = (val) => {
        const date = new Date(val);
        const formatDate = moment(date).format('YYYY-MM-DD');
        setValue(val ? formatDate : '');
    };
    useEffect(() => {
        if (autoFocus) {
            document.getElementById('datePicker')?.focus();
        }
    }, [autoFocus]);

    return (
        <FormControl classes={{ root: classes.rootFormControl }}>
            {label && (
                <label className={classes.label} htmlFor={id || name} id={id}>
                    {label}
                </label>
            )}
            <div
                className={!customClasses && classes.datePickerContainer}
                onKeyDown={(e) => {
                    if (e.key === 'Tab') {
                        datePickerRef.current.closeCalendar();
                    }
                }}
            >
                <DatePicker
                    value={isRangePicker ? rangeValue : new Date(value)}
                    className={classes.datePickerParent}
                    inputClass={`${customClasses ? customClasses : classes.datePicker} ${
                        classes.inputClass
                    }`}
                    id="datePicker"
                    format={format}
                    calendar={lang === 'fa' ? persian : gregorian}
                    locale={lang === 'fa' ? persian_fa : gregorian_en}
                    calendarPosition={isLeft ? left : right}
                    onChange={onChange ? onChange : handleChange}
                    arrow={false}
                    minDate={min}
                    maxDate={max}
                    range={isRangePicker}
                    numberOfMonths={isRangePicker && 2}
                    disabled={disabled}
                    plugins={
                        isRangePicker
                            ? [<Footer position="bottom" key="footer" />]
                            : hasTimePicker
                            ? [<TimePicker position="bottom" key="timePicker" hideSeconds />]
                            : null
                    }
                    zIndex={2000}
                    ref={datePickerRef}
                    portal={usePortal}
                    {...props}
                />
                {!customClasses && (
                    <>
                        <CalendarIcon
                            className={classes.calendarIcon}
                            fill="#ccc"
                            width="21px"
                            height="21px"
                        />
                        <div
                            className={classes.arrowIcon}
                            onClick={() =>
                                !datePickerRef.current.isOpen &&
                                datePickerRef.current.openCalendar()
                            }
                        >
                            <ArrowBottomIcon />
                        </div>
                        {placeholder && !rangeValue[0]?.format() && (
                            <span className={classes.placeholder}>{placeholder}</span>
                        )}
                    </>
                )}
            </div>
            {errors && (
                <div className={classes.errorContainer}>
                    <span> {messages.validations.required}</span>
                </div>
            )}
        </FormControl>
    );
}
CalendarInput.propTypes = {
    id: PropTypes.any,
    name: PropTypes.string,
    label: PropTypes.string,
    setValue: PropTypes.func,
    errors: PropTypes.bool,
    value: PropTypes.string,
    noStyle: PropTypes.bool,
    hasTimePicker: PropTypes.bool,
    customClasses: PropTypes.string,
    onChange: PropTypes.func,
    usePortal: PropTypes.bool,
    min: PropTypes.instanceOf(Date),
    max: PropTypes.instanceOf(Date),
    format: PropTypes.string,
    autoFocus: PropTypes.bool,
    isRangePicker: PropTypes.bool,
    rangeValue: PropTypes.array,
    isLeft: PropTypes.bool,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool
};
